import React from 'react';
import Notification from '../UI/MessageBoxs/Notification';
import { connect } from 'react-redux';
import MessageBox from '../UI/Modals/MessageBox';
import errorCheck from '../../images/error-check.svg';
import ProgressBar from '../UI/Others/ProgressBar';
import classes from './ErrorBoundary.module.css';
import { reportClientError } from '../../functions/reportClientError';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.log('ErrorBoundary caught an error:', error, errorInfo);
    
    // Check if this is a ChunkLoadError - happens when chunks are missing due to stale cache
    const isChunkLoadError = error.name === 'ChunkLoadError' || 
                             error.message?.includes('Loading chunk') ||
                             error.message?.includes('ChunkLoadError');
    
    if (isChunkLoadError) {
      // Check if we've already attempted a reload for this chunk error
      // Use sessionStorage to persist across component remounts and prevent multiple reloads
      const reloadKey = 'chunkErrorReloadAttempted';
      let hasAttemptedReload = false;
      
      try {
        hasAttemptedReload = sessionStorage.getItem(reloadKey) === 'true';
      } catch (e) {
        // sessionStorage might not be available (very rare, but handle gracefully)
        console.warn('sessionStorage not available:', e);
      }
      
      if (!hasAttemptedReload) {
        try {
          sessionStorage.setItem(reloadKey, 'true');
        } catch (e) {
          console.warn('Could not set sessionStorage:', e);
        }
        
        console.log('ChunkLoadError detected - forcing hard reload to fetch fresh chunks');
        
        // Clear the flag after 10 seconds in case reload fails (allows retry)
        // Note: This timeout will be cleared when page unloads, so it's safe
        setTimeout(() => {
          try {
            sessionStorage.removeItem(reloadKey);
          } catch (e) {
            // Ignore errors when clearing
          }
        }, 10000);
        
        // Delay reload slightly to ensure user sees the message (1.5 seconds)
        // This matches the ProgressBar duration in the render method
        setTimeout(() => {
          // Force hard reload with cache-busting by adding timestamp to URL
          // This ensures browser fetches fresh chunks from server
          const currentUrl = window.location.href.split('?')[0];
          const cacheBuster = Date.now();
          window.location.href = `${currentUrl}?reload=${cacheBuster}`;
        }, 1500);
      }
      // Don't report ChunkLoadErrors to server - they're expected after deployments/cache issues
      // This prevents spam of error emails
      return;
    }
    
    // Check if user is logged out - if so, show message and redirect
    const isAnyUserLoggedIn = this.props.isLoggedIn || 
                               this.props.portalUserLoggedIn || 
                               this.props.hubUserLoggedIn || 
                               this.props.donorLoggedIn || 
                               this.props.parentLoggedIn;
    
    // Always email er@adsfo.com (logged in or out). ChunkLoadErrors are skipped inside reportClientError.
    reportClientError({
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
    }, error?.name);

    if (!isAnyUserLoggedIn) {
      // User is logged out - reload after showing modal (modal is rendered in render method)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }

  render() {
    if (this.state.hasError) {
      // Check if this is a ChunkLoadError
      const isChunkLoadError = this.state.error?.name === 'ChunkLoadError' || 
                               this.state.error?.message?.includes('Loading chunk') ||
                               this.state.error?.message?.includes('ChunkLoadError');
      
      if (isChunkLoadError) {
        // Show a user-friendly message for chunk load errors
        // The page will reload automatically (handled in componentDidCatch)
        return (
          <MessageBox onClose={() => {}} className={classes.sessionExpiredModal}>
            <div className={classes.sessionExpiredContent}>
              <img src={errorCheck} alt='' className={classes.sessionExpiredIcon}/>
              <h3 className={classes.sessionExpiredTitle}>Updating Application</h3>
              <p className={classes.sessionExpiredMessage}>A new version is available. Reloading the page...</p>
              <ProgressBar duration={1500} />
            </div>
          </MessageBox>
        );
      }
      
      // Check again in render as a backup (in case componentDidCatch didn't handle it)
      const isAnyUserLoggedIn = this.props.isLoggedIn || 
                                 this.props.portalUserLoggedIn || 
                                 this.props.hubUserLoggedIn || 
                                 this.props.donorLoggedIn || 
                                 this.props.parentLoggedIn;
      
      if (!isAnyUserLoggedIn) {
        // User is logged out - render centered modal (not broken children)
        // This allows message to show without rendering the broken component tree
        return (
          <MessageBox onClose={() => {}} className={classes.sessionExpiredModal}>
            <div className={classes.sessionExpiredContent}>
              <img src={errorCheck} alt='' className={classes.sessionExpiredIcon}/>
              <h3 className={classes.sessionExpiredTitle}>Session Expired</h3>
              <p className={classes.sessionExpiredMessage}>You were logged out. Redirecting to login page...</p>
              <ProgressBar duration={3000} />
            </div>
          </MessageBox>
        );
      }
      
      return <Notification message={{status: 'error', title: 'Something went wrong', message: 'The error has been reported to our support team. Please try refreshing the page.'}}></Notification>; // Customize this fallback UI if needed
    }

    return this.props.children;
  }
}

const mapStateToProps = (state) => ({
    baseUrl: state.auth.url,
    isLoggedIn: state.auth.isLoggedIn,
    portalUserLoggedIn: state.auth.portalUserLoggedIn,
    hubUserLoggedIn: state.auth.hubUserLoggedIn,
    donorLoggedIn: state.auth.donorLoggedIn,
    parentLoggedIn: state.auth.parentLoggedIn
  });

  export default connect(mapStateToProps)(ErrorBoundary);
